import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { useUserStore } from '../../store';

const RESULT_CHIP_COLOR = {
  OK: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  FATAL: 'error',
};

function formatDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fi-FI');
}

export default function BoschDiagnosticsDialog({ open, handleClose, maintenance, setMaintenance, onSaved }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  const [phase, setPhase] = useState('list'); // 'list' | 'preview'
  const [fetchStatus, setFetchStatus] = useState('idle'); // idle | loading | success | error
  const [fetchError, setFetchError] = useState('');
  const [jobcards, setJobcards] = useState([]);
  const [previewStatus, setPreviewStatus] = useState('idle');
  const [previewError, setPreviewError] = useState('');
  const [tasks, setTasks] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchPreview = useCallback(
    async (jobcard) => {
      setPhase('preview');
      setPreviewStatus('loading');
      setPreviewError('');
      setSaveError('');
      try {
        const resp = await axios.get(`/api/bosch-core/jobcards/${jobcard.id}/preview/`, {
          params: {
            organization: orgId,
            order_nr: jobcard.order_nr || '',
            name: jobcard.name || '',
            mileage: jobcard.mileage ?? '',
            created_at: jobcard.created_at || '',
          },
        });
        setTasks(resp.data.tasks || []);
        setText(resp.data.generated_text || '');
        setPreviewStatus('success');
      } catch (err) {
        setPreviewError(err.response?.data?.error || t('bosch.fetchError'));
        setPreviewStatus('error');
      }
    },
    [orgId, t]
  );

  const fetchJobcards = useCallback(async () => {
    setFetchStatus('loading');
    setFetchError('');
    try {
      const resp = await axios.get(`/api/bosch-core/maintenances/${maintenance.id}/jobcards/`, {
        params: { organization: orgId },
      });
      const cards = resp.data.jobcards || [];
      setJobcards(cards);
      setFetchStatus('success');
      if (cards.length === 1) {
        fetchPreview(cards[0]);
      }
    } catch (err) {
      setFetchError(err.response?.data?.error || t('bosch.fetchError'));
      setFetchStatus('error');
    }
  }, [maintenance?.id, orgId, t, fetchPreview]);

  useEffect(() => {
    if (open && maintenance?.id) {
      setPhase('list');
      setJobcards([]);
      setTasks([]);
      setText('');
      setPreviewStatus('idle');
      setSaveError('');
      fetchJobcards();
    }
  }, [open, maintenance?.id, fetchJobcards]);

  const handleSaveAsNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      const formData = new FormData();
      formData.append('text', text.trim());
      formData.append('note_type', 'internal');
      const resp = await axios.post(`/api/maintenances/${maintenance.id}/add_note/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMaintenance(resp.data);
      onSaved?.(t('bosch.savedAsNote'));
      handleClose();
    } catch (err) {
      setSaveError(err.response?.data?.error || t('bosch.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleAppendToDescription = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      const existing = maintenance?.description || '';
      const description = existing ? `${existing}\n\n${text.trim()}` : text.trim();
      const resp = await axios.patch(`/api/maintenances/${maintenance.id}/`, {
        description,
        show_description_to_customer: maintenance?.show_description_to_customer || false,
      });
      setMaintenance(resp.data);
      onSaved?.(t('bosch.savedToDescription'));
      handleClose();
    } catch (err) {
      setSaveError(err.response?.data?.error || t('bosch.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const showBack = phase === 'preview' && jobcards.length > 1;

  return (
    <Dialog open={open} onClose={saving ? undefined : handleClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showBack && (
          <IconButton size="small" onClick={() => setPhase('list')} data-testid="bosch-back">
            <ArrowBackOutlined fontSize="small" />
          </IconButton>
        )}
        {t('bosch.dialogTitle')}
        <IconButton size="small" sx={{ ml: 'auto' }} onClick={handleClose} disabled={saving}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {phase === 'list' && (
          <>
            {fetchStatus === 'loading' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {fetchStatus === 'error' && <Alert severity="error" data-testid="bosch-fetch-error">{fetchError}</Alert>}
            {fetchStatus === 'success' && jobcards.length === 0 && (
              <Alert severity="info">{t('bosch.noJobcards')}</Alert>
            )}
            {fetchStatus === 'success' && jobcards.length > 1 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('bosch.selectJobcard')}
                </Typography>
                <List data-testid="bosch-jobcard-list">
                  {jobcards.map((jc) => (
                    <ListItemButton key={jc.id} onClick={() => fetchPreview(jc)} sx={{ borderRadius: '10px' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <span>{jc.order_nr || jc.name || jc.id}</span>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={jc.state === 'OPEN' ? t('bosch.stateOpen') : t('bosch.stateClosed')}
                              color={jc.state === 'OPEN' ? 'info' : 'default'}
                            />
                          </Box>
                        }
                        secondary={[
                          formatDate(jc.created_at),
                          jc.mileage ? `${jc.mileage} km` : '',
                          jc.assigned_employee,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
          </>
        )}

        {phase === 'preview' && (
          <>
            {previewStatus === 'loading' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {previewStatus === 'error' && <Alert severity="error">{previewError}</Alert>}
            {previewStatus === 'success' && (
              <>
                {tasks.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {tasks.map((task) => (
                      <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {task.task_info || task.task_type || task.description}
                        </Typography>
                        {task.result_label && (
                          <Chip
                            size="small"
                            label={task.result_label}
                            color={RESULT_CHIP_COLOR[task.result_code] || 'default'}
                          />
                        )}
                        {(task.dtcs || []).map((dtc) => (
                          <Chip key={dtc.code} size="small" variant="outlined" label={dtc.code} />
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('bosch.previewHelp')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={8}
                  maxRows={20}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  inputProps={{ 'data-testid': 'bosch-preview-text' }}
                />
                {saveError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {saveError}
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={handleClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        {phase === 'preview' && previewStatus === 'success' && (
          <>
            <Button
              variant="outlined"
              onClick={handleAppendToDescription}
              disabled={saving || !text.trim()}
              data-testid="bosch-save-description"
            >
              {saving ? <CircularProgress size={16} /> : t('bosch.appendToDescription')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAsNote}
              disabled={saving || !text.trim()}
              data-testid="bosch-save-note"
            >
              {saving ? <CircularProgress size={16} /> : t('bosch.saveAsNote')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
